"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  EXPRESSION_ERROR_STRINGS,
  expressionErrorMessage,
} from "@/components/tools/calculus/expression-errors";
import { readConstant } from "@/components/tools/calculus/read-constant";
import { RootResults } from "@/components/tools/calculus/root-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type Node,
  substitute,
  variable as variableNode,
} from "@/lib/tool-kit/calculus/ast";
import { differentiate } from "@/lib/tool-kit/calculus/differentiate";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { parseExpression } from "@/lib/tool-kit/calculus/parse";

/** English strings. A translated content file overrides these through `ui`. */
export const NEWTONS_METHOD_CALCULATOR_STRINGS = {
  functionLabel: "Function f(x), solving f(x) = 0",
  functionHint:
    "Use + − * / ^ and brackets. To solve g(x) = h(x), enter g(x) − h(x). Functions: sin, cos, tan, exp, ln, log (base 10), sqrt, abs and more. Constants: pi and e.",
  methodLabel: "Method",
  methods: {
    newton: "Newton's method",
    secant: "Secant method",
    bisection: "Bisection",
  },
  startLabel: "Starting guess x₀",
  secondLabel: "Second starting guess x₁",
  leftLabel: "Left end a of the bracket",
  rightLabel: "Right end b of the bracket",
  bracketHint: "f(a) and f(b) must have opposite signs.",
  toleranceLabel: "Stop when the step is below",
  toleranceOptions: {
    "1e-4": "0.0001",
    "1e-6": "0.000001",
    "1e-8": "0.00000001",
    "1e-10": "0.0000000001",
    "1e-12": "0.000000000001",
  },
  iterationsLabel: "Maximum iterations",
  results: "Results",
  rootLabel: "Root estimate",
  residualLabel: "|f(root)|",
  stepsLabel: "Iterations",
  reasonLabel: "Stopped because",
  reasons: {
    converged: "the step fell below the tolerance",
    "zero-derivative": "f′ is zero",
    undefined: "f is not defined",
    diverged: "the iterates ran away",
    cycle: "the iterates cycle",
    "max-iterations": "the iteration limit was reached",
    "no-bracket": "no sign change in the bracket",
    "flat-secant": "the secant line is flat",
  },
  reasonNotes: {
    "zero-derivative":
      "Newton's method divides by f′(x), which is 0 here, so the tangent line never meets the x-axis. Move the starting guess away from this point.",
    undefined:
      "f has no real value at an iterate, for example a logarithm or square root of a negative number. Choose a starting guess inside the domain, or use bisection on a bracket.",
    diverged:
      "The iterates grew without bound. The starting guess may be too far from a root, or f may have no root nearby (like arctan x from x₀ = 2). Try a guess closer to where the graph crosses the axis.",
    cycle:
      "The iterates bounce between the same values forever. Try a different starting guess, or use bisection, which cannot cycle.",
    "max-iterations":
      "The method had not settled within the limit. If the graph never reaches the x-axis there may be no real root; otherwise raise the limit or change the starting guess.",
    "no-bracket":
      "f(a) and f(b) have the same sign, so bisection cannot tell that a root lies between them. Choose a and b on either side of a crossing of the x-axis.",
    "flat-secant":
      "f(x₀) and f(x₁) are equal, so the secant line is horizontal and has no x-intercept. Choose different starting guesses.",
  },
  formulaHeading: "The formula",
  formulas: {
    newton: "xₙ₊₁ = xₙ − f(xₙ) / f′(xₙ), with f′(x) = {derivative}",
    secant: "xₙ₊₁ = xₙ − f(xₙ)·(xₙ − xₙ₋₁) / (f(xₙ) − f(xₙ₋₁))",
    bisection:
      "Take the midpoint m = (a + b)/2, keep the half where f changes sign, and repeat.",
  },
  tableHeading: "Iteration table",
  columnN: "n",
  columnX: "xₙ",
  columnFx: "f(xₙ)",
  columnDfx: "f′(xₙ)",
  columnNext: "xₙ₊₁",
  columnStep: "|xₙ₊₁ − xₙ|",
  columnPrevious: "xₙ₋₁",
  columnFPrevious: "f(xₙ₋₁)",
  columnA: "a",
  columnB: "b",
  columnMid: "midpoint m",
  columnFMid: "f(m)",
  columnWidth: "width b − a",
  notesHeading: "Convergence",
  orderNote:
    "Observed order of convergence ≈ {order}. Newton's method has order 2 at a simple root (the number of correct digits roughly doubles each step), the secant method about 1.62, and bisection 1 (one more bit per step).",
  ratioNote:
    "The error shrinks by a factor of about {ratio} each step, which is linear convergence.",
  multiplicityNote:
    "That is the signature of a root of multiplicity {m}: Newton's method slows to linear convergence there. Apply it to f/f′ for quadratic convergence again.",
  bisectionNote:
    "Bisection needs about log₂((b − a)/tolerance) steps and always converges once a root is bracketed.",
  compareHeading: "The three methods side by side",
  columnMethod: "Method",
  columnRoot: "Root",
  columnResidual: "|f(root)|",
  columnSteps: "Steps",
  columnOutcome: "Outcome",
  compareNote:
    "Secant uses x₁ = {second}; bisection uses the bracket [{a}, {b}] (found near x₀ if you gave none).",
  noBracketFound: "none found near x₀",
  graphHeading: "Graph",
  graphTitle: "Graph of f(x) with the {method} iterates marked on the x-axis",
  seriesFunction: "y = f(x)",
  seriesSteps: "{method} steps",
  seriesRoot: "Root",
  rootLabelText: "x ≈ {x}",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  pointInvalid:
    "Enter {field} as a number or constant such as 1, −2.5 or pi/2.",
  iterationsInvalid:
    "Enter the maximum iterations as a whole number from 1 to 100.",
  oneVariable: "Use a function of one variable, such as x^2 − 2.",
  expressionErrors: EXPRESSION_ERROR_STRINGS,
} as const;

export type RootStrings = typeof NEWTONS_METHOD_CALCULATOR_STRINGS;
export type RootMethodName = "newton" | "secant" | "bisection";
export type ToleranceKey = keyof RootStrings["toleranceOptions"];

export type RootView =
  | { kind: "problem"; message: string }
  | {
      kind: "ok";
      ast: Node;
      derivative: Node;
      f: (x: number) => number;
      df: (x: number) => number;
      method: RootMethodName;
      start: number;
      second: number;
      left: number | null;
      right: number | null;
      tolerance: number;
      maxIterations: number;
      decimals: boolean;
    };

const TOLERANCES = Object.keys(
  NEWTONS_METHOD_CALCULATOR_STRINGS.toleranceOptions
) as ToleranceKey[];
const METHODS: readonly RootMethodName[] = ["newton", "secant", "bisection"];

type Inputs = Record<
  | "text"
  | "startText"
  | "secondText"
  | "leftText"
  | "rightText"
  | "iterationsText",
  string
> & { method: RootMethodName; tolerance: ToleranceKey };

/** Which numeric fields the chosen method actually uses. */
function fieldsFor(
  method: RootMethodName,
  inputs: Inputs,
  strings: RootStrings
) {
  if (method === "bisection") {
    return [
      ["left", inputs.leftText, strings.leftLabel],
      ["right", inputs.rightText, strings.rightLabel],
    ] as const;
  }
  const first = ["start", inputs.startText, strings.startLabel] as const;
  return method === "secant"
    ? ([first, ["second", inputs.secondText, strings.secondLabel]] as const)
    : ([first] as const);
}

function readFields(
  inputs: Inputs,
  strings: RootStrings
): { values: Record<string, number> } | { error: string } {
  const values: Record<string, number> = {};
  for (const [key, text, label] of fieldsFor(inputs.method, inputs, strings)) {
    const read = readConstant(text);
    if (read === null || read === "invalid") {
      return { error: t(strings.pointInvalid, { field: label }) };
    }
    values[key] = read.value;
  }
  return { values };
}

function buildView(inputs: Inputs, strings: RootStrings): RootView {
  const parsed = parseExpression(inputs.text);
  if (!parsed.ok) {
    return {
      kind: "problem",
      message: expressionErrorMessage(parsed.error, strings.expressionErrors),
    };
  }
  if (parsed.variables.length > 1) {
    return { kind: "problem", message: strings.oneVariable };
  }
  const maxIterations = parseIntegerInput(inputs.iterationsText);
  if (maxIterations === null || maxIterations < 1 || maxIterations > 100) {
    return { kind: "problem", message: strings.iterationsInvalid };
  }
  const read = readFields(inputs, strings);
  if ("error" in read) {
    return { kind: "problem", message: read.error };
  }
  const { values } = read;
  const original = parsed.variables[0] ?? "x";
  const ast =
    original === "x"
      ? parsed.ast
      : substitute(parsed.ast, original, variableNode("x"));
  const derivative = differentiate(ast, "x").derivative;
  return {
    kind: "ok",
    ast,
    derivative,
    f: compile(ast, "x"),
    df: compile(derivative, "x"),
    method: inputs.method,
    start: values.start ?? Number.NaN,
    second: values.second ?? Number.NaN,
    left: values.left ?? null,
    right: values.right ?? null,
    tolerance: Number(inputs.tolerance),
    maxIterations,
    decimals: parsed.usesDecimals,
  };
}

export default function NewtonsMethodCalculatorWidget() {
  const strings = useToolUi(NEWTONS_METHOD_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("x^2 - 2");
  const [method, setMethod] = useState<RootMethodName>("newton");
  const [startText, setStartText] = useState("1");
  const [secondText, setSecondText] = useState("2");
  const [leftText, setLeftText] = useState("1");
  const [rightText, setRightText] = useState("2");
  const [tolerance, setTolerance] = useState<ToleranceKey>("1e-10");
  const [iterationsText, setIterationsText] = useState("50");
  const view = useMemo(
    () =>
      buildView(
        {
          text,
          method,
          startText,
          secondText,
          leftText,
          rightText,
          tolerance,
          iterationsText,
        },
        strings
      ),
    [
      text,
      method,
      startText,
      secondText,
      leftText,
      rightText,
      tolerance,
      iterationsText,
      strings,
    ]
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            hint={strings.functionHint}
            id="nm-function"
            label={strings.functionLabel}
            mono
            onChange={setText}
            value={text}
          />
        </div>
        <SelectField
          id="nm-method"
          label={strings.methodLabel}
          onChange={setMethod}
          options={METHODS.map((value) => ({
            value,
            label: strings.methods[value],
          }))}
          value={method}
        />
        {method === "bisection" ? (
          <>
            <TextField
              hint={strings.bracketHint}
              id="nm-left"
              label={strings.leftLabel}
              mono
              onChange={setLeftText}
              value={leftText}
            />
            <TextField
              id="nm-right"
              label={strings.rightLabel}
              mono
              onChange={setRightText}
              value={rightText}
            />
          </>
        ) : (
          <TextField
            id="nm-start"
            label={strings.startLabel}
            mono
            onChange={setStartText}
            value={startText}
          />
        )}
        {method === "secant" ? (
          <TextField
            id="nm-second"
            label={strings.secondLabel}
            mono
            onChange={setSecondText}
            value={secondText}
          />
        ) : null}
        <SelectField
          id="nm-tolerance"
          label={strings.toleranceLabel}
          onChange={setTolerance}
          options={TOLERANCES.map((value) => ({
            value,
            label: strings.toleranceOptions[value],
          }))}
          value={tolerance}
        />
        <TextField
          id="nm-iterations"
          label={strings.iterationsLabel}
          mono
          numeric
          onChange={setIterationsText}
          value={iterationsText}
        />
      </div>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {view.kind === "problem" ? (
          <ErrorNotice>{view.message}</ErrorNotice>
        ) : null}
        {view.kind === "ok" ? (
          <RootResults fmt={fmt} strings={strings} view={view} />
        ) : null}
      </section>
    </>
  );
}
